import { useMemo } from 'react'
import groupBy from 'lodash/groupBy'
import round from '../util/round'

const conditionallyVisibleFields = {
  net_weight_in_tons: [
    'net_weight_in_tons',
    'tonnage_pay_rate_in_dollars',
  ],
  regular_time_in_hours: [
    'regular_time_in_hours',
    'regular_pay_rate_in_dollars',
  ],
  haul_time_in_hours: [
    'haul_time_in_hours',
    'haul_pay_rate_in_dollars',
  ],
  convey_time_in_hours: [
    'convey_time_in_hours',
    'convey_pay_rate_in_dollars',
  ],
  equipment_time_in_hours: [
    'equipment_time_in_hours',
    'equipment_pay_rate_in_dollars',
  ],
  wait_time_in_hours: [
    'wait_time_in_hours',
    'wait_pay_rate_in_dollars',
  ],
  overtime_in_hours: [
    'overtime_in_hours',
    'overtime_pay_rate_in_dollars',
  ],
  doubletime_in_hours: [
    'doubletime_in_hours',
    'doubletime_pay_rate_in_dollars',
  ],
  equipment_rate_in_dollars: [
    'equipment_rate_in_dollars',
  ],
  fuel_surcharge_in_dollars: [
    'fuel_surcharge_in_dollars',
  ],
  material_surcharge_in_dollars: [
    'material_surcharge_in_dollars',
  ],
}

const usePayRollupTickets = ({ tonnageTickets, hourlyTickets }) => {
  return useMemo(() => {
    const tickets = [
      tonnageTickets,
      hourlyTickets
    ].reduce((accum, tickets, index) => {
      const type = index === 0 ? 't' : 'h'
      return [
        ...accum,
        ...tickets.map(t => ({ ...t, type: index, id: `${type}-${t.id}` })),
      ]
    }, [])

    const sorted = tickets.sort((a, b) => {
      // date asc
      if (a.date > b.date) return 1
      if (a.date < b.date) return -1

      // tonnage first
      if (a.type > b.type) return 1
      if (a.type < b.type) return -1

      // ticket_number asc
      if (a.ticket_number > b.ticket_number) return 1
      if (a.ticket_number < b.ticket_number) return -1
      return 0
    })

    const grouped = groupBy(sorted, 'date')

    const conditionallyVisibleFieldsValues = Object.values(conditionallyVisibleFields).flat()
    const conditionallyVisibleFieldsKeys = Object.keys(conditionallyVisibleFields)
    const conditionalFieldsToCheck = [...conditionallyVisibleFieldsKeys]
    const visibleFields = []

    const collection = Object.entries(grouped).reduce((accum, [date, tickets]) => {
      const sums = tickets.reduce((_accum, curr) => {
        for (const field of [...conditionalFieldsToCheck]) {
          if (curr[field]) {
            conditionalFieldsToCheck.splice(conditionalFieldsToCheck.indexOf(field), 1)
            conditionallyVisibleFields[field].forEach((f) => {
              visibleFields[conditionallyVisibleFieldsValues.indexOf(f)] = f
            })
          }
        }

        return {
          total_pay_in_dollars: _accum.total_pay_in_dollars + curr.total_pay_in_dollars,
          haul_time_in_hours: _accum.haul_time_in_hours + curr.haul_time_in_hours,
          convey_time_in_hours: _accum.convey_time_in_hours + curr.convey_time_in_hours,
          equipment_time_in_hours: _accum.equipment_time_in_hours + curr.equipment_time_in_hours,
          wait_time_in_hours: _accum.wait_time_in_hours + curr.wait_time_in_hours,
          regular_time_in_hours: _accum.regular_time_in_hours + curr.regular_time_in_hours,
          overtime_in_hours: _accum.overtime_in_hours + curr.overtime_in_hours,
          doubletime_in_hours: _accum.doubletime_in_hours + curr.doubletime_in_hours,
          net_weight_in_tons: _accum.net_weight_in_tons + curr.net_weight_in_tons,
        }
      }, {
        total_pay_in_dollars: 0,
        haul_time_in_hours: 0,
        convey_time_in_hours: 0,
        equipment_time_in_hours: 0,
        wait_time_in_hours: 0,
        regular_time_in_hours: 0,
        overtime_in_hours: 0,
        doubletime_in_hours: 0,
        net_weight_in_tons: 0,
      })

      const roundedSums = Object.entries(sums).reduce((accum, [key, value]) => ({ ...accum, [key]: round(value, 2) }), {})

      return [
        ...accum,
        {
          data: tickets,
          subtotal: {
            ticket_number: tickets.length,
            date: date,
            ...roundedSums,
          },
        }
      ]
    }, [])

    return {
      collection,
      conditionallyVisibleFields: visibleFields.filter(Boolean),
    }
  }, [tonnageTickets, hourlyTickets])
}

export default usePayRollupTickets
